import { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { useMutation, useQuery } from "react-query";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  addChatHelpContent,
  getChatHelpContent,
  updateChatHelpContent,
} from "../../api";
import { RegionCode } from "../../constants/constant";
import { snackActions } from "../../helpers/SnackUtilsConfigurator";

const ChatHelp = () => {
  const [bodyTextUK, setBodyTextUK] = useState("");
  const [bodyTextUS, setBodyTextUS] = useState("");
  const [bodyTextFRCA, setBodyTextFRCA] = useState("");
  const [bodyTextFRFR, setBodyTextFRFR] = useState("");

  const stripHtml = (html: any) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  const { data: chatHelpData, isLoading: helpLoading } = useQuery(
    "chat-help",
    getChatHelpContent,
  );

  const {
    mutateAsync: addChatHelp,
    isLoading: isAddingHelp,
    error: addChatHelpError,
  } = useMutation("add-chat-help", addChatHelpContent);

  const {
    mutateAsync: updateChatHelp,
    isLoading: isUpdatingHelp,
    error: updateHelpError,
  } = useMutation("update-chat-help", updateChatHelpContent);

  useEffect(() => {
    if (chatHelpData?.result?.value) {
      try {
        const parsedData = JSON.parse(chatHelpData.result.value);

        // Check if parsedData is an array or object
        if (Array.isArray(parsedData)) {
          parsedData.forEach((item: any) => {
            if (item[RegionCode.RegionUK]) {
              setBodyTextUK(item[RegionCode.RegionUK]);
            }
            if (item[RegionCode.RegionUS]) {
              setBodyTextUS(item[RegionCode.RegionUS]);
            }
            if (item[RegionCode.RegionFR_CA]) {
              setBodyTextFRCA(item[RegionCode.RegionFR_CA]);
            }
            if (item[RegionCode.RegionFR_FR]) {
              setBodyTextFRFR(item[RegionCode.RegionFR_FR]);
            }
          });
        } else if (typeof parsedData === "object" && parsedData !== null) {
          // If it's a direct object
          if (parsedData[RegionCode.RegionUK]) {
            setBodyTextUK(parsedData[RegionCode.RegionUK]);
          }
          if (parsedData[RegionCode.RegionUS]) {
            setBodyTextUS(parsedData[RegionCode.RegionUS]);
          }
          if (parsedData[RegionCode.RegionFR_CA]) {
            setBodyTextFRCA(parsedData[RegionCode.RegionFR_CA]);
          }
          if (parsedData[RegionCode.RegionFR_FR]) {
            setBodyTextFRFR(parsedData[RegionCode.RegionFR_FR]);
          }
        }
      } catch (error) {
        console.error("Error parsing help data:", error);
        // Handle the case where the value is not valid JSON
        // You might want to set default values or show an error message
      }
    }
  }, [chatHelpData]);

  const handleSubmit = async () => {
    let bodyParams = {
      id: chatHelpData?.result ? chatHelpData?.result?.id : 0,
      key: "Chat_Help_All_region",
      value: JSON.stringify({
        [RegionCode.RegionUK]: bodyTextUK, // "en-GB": bodyTextUK
        [RegionCode.RegionUS]: bodyTextUS, // "en-US": bodyTextUS
        [RegionCode.RegionFR_CA]: bodyTextFRCA, // "fr-CA": bodyTextFRCA
        [RegionCode.RegionFR_FR]: bodyTextFRFR, // "fr-FR": bodyTextFRFR
      }),
      description: null,
    };

    let response = chatHelpData?.result
      ? await updateChatHelp(bodyParams)
      : await addChatHelp(bodyParams);

    if (response?.result) {
      snackActions.success(
        response?.message ??
          `Chat Help Content ${
            chatHelpData?.result ? "Updated" : "Added"
          } Successfully!`,
      );
    } else {
      snackActions.error(response?.message ?? "Something went wrong!");
    }
  };

  return (
    <div>
      <div className="card mb-5 mb-xl-10 p-10">
        <div className="notice d-flex bg-light-primary rounded border-primary border border-dashed p-6 mb-10">
          <div className="d-flex flex-stack flex-grow-1">
            <div className="fw-semibold">
              <div className="fs-6 text-gray-700">
                <i className="fas fa-exclamation-triangle text-primary fs-2 me-4"></i>
                <span className="fs-4 fw-bold text-primary me-2">
                  Important:
                </span>
                <span className="fs-5 fw-semibold text-gray-800">
                  Multi-Language Support Required
                </span>
              </div>
              <div className="fs-7 text-gray-600 mt-3">
                <div className="d-flex align-items-center mb-2">
                  <i className="fas fa-globe text-primary me-2"></i>
                  <span>
                    This section supports multiple languages for help content
                  </span>
                </div>
                <div className="d-flex align-items-center mb-2">
                  <i className="fas fa-check-circle text-success me-2"></i>
                  <span>
                    Ensure help text is added for all available languages
                  </span>
                </div>
                <div className="d-flex align-items-center">
                  <i className="fas fa-users text-warning me-2"></i>
                  <span>
                    Users will see content based on their selected region and
                    language in the mobile app
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* United Kingdom Section */}
        <div className="mb-8">
          <div className="d-flex align-items-center mb-4">
            <span className="me-3 fs-2">🇬🇧</span>
            <h3 className="mb-0 text-gray-800">
              Chat Help - United Kingdom (en-GB)
            </h3>
          </div>
          <ReactQuill
            theme="snow"
            placeholder="Write help content for UK users..."
            modules={ChatHelp.modules}
            formats={ChatHelp.formats}
            style={{ height: "40vh", paddingBottom: "4rem" }}
            value={bodyTextUK || ""}
            onChange={(e) => setBodyTextUK(e)}
          />
        </div>

        {/* United States Section */}
        <div className="mb-8">
          <div className="d-flex align-items-center mb-4">
            <span className="me-3 fs-2">🇺🇸</span>
            <h3 className="mb-0 text-gray-800">
              Chat Help - United States (en-US)
            </h3>
          </div>
          <ReactQuill
            theme="snow"
            placeholder="Write help content for US users..."
            modules={ChatHelp.modules}
            formats={ChatHelp.formats}
            style={{ height: "40vh", paddingBottom: "4rem" }}
            value={bodyTextUS || ""}
            onChange={(e) => setBodyTextUS(e)}
          />
        </div>

        {/* French Canada Section */}
        <div className="mb-8">
          <div className="d-flex align-items-center mb-4">
            <span className="me-3 fs-2">🇨🇦</span>
            <h3 className="mb-0 text-gray-800">
              Chat Help - Français (Canada) (fr-CA)
            </h3>
          </div>
          <ReactQuill
            theme="snow"
            placeholder="Rédigez le contenu d'aide pour les utilisateurs canadiens..."
            modules={ChatHelp.modules}
            formats={ChatHelp.formats}
            style={{ height: "40vh", paddingBottom: "4rem" }}
            value={bodyTextFRCA || ""}
            onChange={(e) => setBodyTextFRCA(e)}
          />
        </div>

        {/* French France Section */}
        <div className="mb-8">
          <div className="d-flex align-items-center mb-4">
            <span className="me-3 fs-2">🇫🇷</span>
            <h3 className="mb-0 text-gray-800">
              Chat Help - Français (France) (fr-FR)
            </h3>
          </div>
          <ReactQuill
            theme="snow"
            placeholder="Rédigez le contenu d'aide pour les utilisateurs français..."
            modules={ChatHelp.modules}
            formats={ChatHelp.formats}
            style={{ height: "40vh", paddingBottom: "4rem" }}
            value={bodyTextFRFR || ""}
            onChange={(e) => setBodyTextFRFR(e)}
          />
        </div>

        <div className="d-flex justify-content-between">
          <Button
            variant="success"
            disabled={
              stripHtml(bodyTextUK).trim().length === 0 &&
              stripHtml(bodyTextUS).trim().length === 0 &&
              stripHtml(bodyTextFRCA).trim().length === 0 &&
              stripHtml(bodyTextFRFR).trim().length === 0
            }
            style={{ width: "120px", marginLeft: "auto", marginBottom: "10px" }}
            onClick={handleSubmit}
          >
            {isAddingHelp || isUpdatingHelp ? "Loading..." : "Save All"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ChatHelp;

ChatHelp.modules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }], // More header levels
    [{ font: [] }], // Font family
    [{ size: ["small", false, "large", "huge"] }], // Font size
    ["bold", "italic", "underline", "strike"], // Text formatting
    [{ color: [] }, { background: [] }], // Text and background colors
    [{ script: "sub" }, { script: "super" }], // Subscript/Superscript
    [{ list: "ordered" }, { list: "bullet" }], // Lists
    [{ indent: "-1" }, { indent: "+1" }], // Indentation
    [{ direction: "rtl" }], // Text direction
    [{ align: [] }], // Text alignment
    ["blockquote", "code-block"], // Blockquote and code block
    ["link"], // Links, images, videos
    ["clean"], // Remove formatting
  ],
};
ChatHelp.formats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "script",
  "list",
  "bullet",
  "indent",
  "direction",
  "align",
  "blockquote",
  "code-block",
  "link",
];
